import { SapiRepository, sapiRepository } from '@repositories/sapi';

import { SapiProductType } from '@app-types/api/sapi';

import { roundStringNumber } from '@helpers/number';

export type MobileProductsCharacteristicsType = Awaited<
    ReturnType<MobileComparatorService['getProductsCharacteristics']>
>;

class MobileComparatorService {
    private sapiRepository: SapiRepository;

    public constructor(sapiRepository: SapiRepository) {
        this.sapiRepository = sapiRepository;
    }

    public getProductsCharacteristics = async (gencodes: string[]) => {
        const uniqueGencodes = [...new Set(gencodes)].slice(0, 3);

        return this.getProducts(uniqueGencodes).then((products) => products.map(this.mapper));
    };

    private async getProducts(gencodes: string[]): Promise<SapiProductType[]> {
        const requests = gencodes.map((gencode) => this.sapiRepository.getProductByGencode<SapiProductType>(gencode));

        return Promise.allSettled(requests).then((results) => {
            return results.filter((result) => result.status === 'fulfilled').map(({ value }) => value);
        });
    }

    readonly mapper = (product: SapiProductType) => {
        return {
            gencode: product.gencode,
            typeId: product.type_id,
            brand: product.manufacturer,
            name: product.name,
            image: product.image,
            price: product.prices.sowo.price,
            urlKey: product.url_key,
            colors: [...new Set(Object.values(product.childs).map((child) => child.color_code))],
            storage: [
                ...new Set(
                    Object.values(product.childs).map((child) =>
                        child.capacity ? child.capacity.replace(/ /g, '\u00A0') : undefined,
                    ),
                ),
            ].join(' / '),
            chargingHead: product.charging_head,
            chargingMinPower: this.formatValue(product.min_load_power, 0, 'Watts'),
            chargingMaxPower: this.formatValue(product.max_load_power, 0, 'Watts'),
            energyLabel: product.energy_label,
            reparability: Number.parseFloat(product.note_reparation ?? '-1'),
            os: product.os,
            osVersion: product.os_version,
            connectivity: this.formatConnectivity(product.max_connection),
            wifi: product.wifi,
            multipleSim: product.sim_multiple,
            eSim: ['esim', 'hybride'].includes(product.sim_type),
            screenSize: this.formatValue(product.diagonal, 1, 'pouces'),
            screenWidth: this.formatValue(product.resolution_width, 0, 'pixels'),
            screenHeight: this.formatValue(product.resolution_height, 0, 'pixels'),
            resolutionSensor: this.formatValue(product.resolution_sensor, 0, 'Mpx'),
            autoFocus: product.auto_focus,
            doubleCamera: product.double_camera,
            batteryCapacity: this.formatValue(product.battery_capacity, 0, 'mA'),
            batteryRemovable: product.battery_removable,
            batteryTalkTime: this.formatValue(product.battery_talk_time, 0, 'h'),
            batteryStandbyTime: this.formatValue(product.battery_standby_time, 0, 'h'),
            processorCore: product.processor_core,
            processorSpeed: this.formatValue(product.processor_speed, 2, 'Ghz'),
            weight: this.formatValue(product.weight, 0, 'g'),
            length: this.formatValue(product.length, 2, 'cm'),
            width: this.formatValue(product.width, 2, 'cm'),
            height: this.formatValue(product.height, 2, 'cm'),
            sar: this.formatValue(product.sar, 3, 'W/kg', true),
            trunkSar: this.formatValue(product.trunk_sar, 3, 'W/kg', true),
            limbsSar: this.formatValue(product.limbs_sar, 3, 'W/kg', true),
        };
    };

    readonly formatValue = (
        value: string | number | undefined,
        precision: number,
        unit: string,
        forceSign: boolean = false,
    ): string | undefined => {
        return value ? `${roundStringNumber(value?.toString(), precision, forceSign)} ${unit}` : undefined;
    };

    readonly formatConnectivity = (maxConnection: string | undefined): string | undefined => {
        if (!maxConnection) {
            return;
        }

        const baseConnection = maxConnection.toUpperCase();
        const suffix = maxConnection === '5g' ? '/4G+' : '';

        return `${baseConnection}${suffix}`;
    };
}

export const mobileComparatorService = new MobileComparatorService(sapiRepository);
